import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Beaker,
  TrendingUp,
  TrendingDown,
  Activity,
  Loader2,
  Info,
  CheckCircle2,
  Clock,
} from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import { api, apiGet, formatLKR, formatPct, formatDate } from "@/lib/api";

const BacktestPage = () => {
  const [availability, setAvailability] = useState(null);
  const [loading, setLoading] = useState(true);
  const [results, setResults] = useState(null);
  const [running, setRunning] = useState(false);

  // Config state
  const [startDate, setStartDate] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 90);
    return d.toISOString().slice(0, 10);
  });
  const [endDate, setEndDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [investment, setInvestment] = useState(100000);
  const [includeBrokerage, setIncludeBrokerage] = useState(true);
  const [minScore, setMinScore] = useState(65);
  const [signalFilter, setSignalFilter] = useState("BUY");

  useEffect(() => {
    let active = true;
    apiGet("/backtest/availability")
      .then((d) => { if (active) setAvailability(d); })
      .catch((e) => console.warn(e))
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, []);

  const isReady = availability?.ready === true;
  const progress = availability ? Math.min(100, (availability.score_signal_days / (availability.target_days || 90)) * 100) : 0;

  const runBacktest = async () => {
    setRunning(true);
    setResults(null);
    try {
      const { data } = await api.post("/backtest/run", {
        start_date: startDate,
        end_date: endDate,
        investment_per_signal: Number(investment),
        include_brokerage: includeBrokerage,
        min_score: Number(minScore),
        signal_filter: signalFilter,
      });
      setResults(data);
      if ((data?.summary?.total_signals || 0) === 0) {
        toast.info("No signals matched the configuration in this date range");
      } else {
        toast.success(`Backtest complete — ${data.summary.total_signals} signals`);
      }
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message || "Backtest failed";
      toast.error(detail);
    } finally {
      setRunning(false);
    }
  };

  if (loading) {
    return <Skeleton className="h-96 w-full" />;
  }

  return (
    <div className="space-y-6 max-w-6xl" data-testid="backtest-page">
      <div>
        <h1 className="text-2xl font-display font-semibold flex items-center gap-2">
          <Beaker className="h-6 w-6 text-primary" />
          Strategy Backtesting
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          Test how our BUY / WATCH / HOLD signals have performed historically.
        </p>
      </div>

      {/* DATA AVAILABILITY INDICATOR */}
      <Card data-testid="backtest-availability-card">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm flex items-center gap-2">
            <Info className="h-4 w-4 text-primary" />
            What we need for backtesting
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3 text-sm">
          {(() => {
            const priceDays = availability?.price_data_days || 0;
            const scoreDays = availability?.score_signal_days || 0;
            const target = availability?.target_days || 90;
            const priceReady = priceDays >= target;
            const scoreReady = scoreDays >= target;
            return (
              <div className="space-y-2.5">
                <div
                  className={`flex items-start gap-2 rounded-md border p-2.5 ${
                    priceReady
                      ? "bg-[hsl(var(--success))]/10 border-[hsl(var(--success))]/30"
                      : "bg-muted border-border"
                  }`}
                  data-testid="backtest-price-history-row"
                >
                  {priceReady ? (
                    <CheckCircle2 className="h-4 w-4 text-[hsl(var(--success))] shrink-0 mt-0.5" />
                  ) : (
                    <Clock className="h-4 w-4 text-muted-foreground shrink-0 mt-0.5" />
                  )}
                  <div className="text-xs flex-1 min-w-0">
                    <div className="font-medium mb-0.5">
                      Price history: <span className="tabular">{priceDays}</span> days
                      {priceReady && (
                        <span className="ml-1.5 text-[hsl(var(--success))] font-semibold">Ready</span>
                      )}
                    </div>
                    {availability?.price_first_date && (
                      <div className="text-muted-foreground">
                        {formatDate(availability.price_first_date)}
                        &nbsp;→&nbsp;
                        {formatDate(availability.price_latest_date)}
                      </div>
                    )}
                  </div>
                </div>
                <div
                  className={`flex items-start gap-2 rounded-md border p-2.5 ${
                    scoreReady
                      ? "bg-[hsl(var(--success))]/10 border-[hsl(var(--success))]/30"
                      : "bg-[hsl(var(--warning))]/10 border-[hsl(var(--warning))]/30"
                  }`}
                  data-testid="backtest-score-history-row"
                >
                  {scoreReady ? (
                    <CheckCircle2 className="h-4 w-4 text-[hsl(var(--success))] shrink-0 mt-0.5" />
                  ) : (
                    <Clock className="h-4 w-4 text-[hsl(var(--warning))] shrink-0 mt-0.5" />
                  )}
                  <div className="text-xs flex-1 min-w-0">
                    <div className="font-medium mb-0.5">
                      Score history:{" "}
                      <span className="tabular">{scoreDays} of {target}</span> days
                      {!scoreReady && (
                        <span className="ml-1.5 text-[hsl(var(--warning))] font-semibold">
                          Collecting…
                        </span>
                      )}
                    </div>
                    {availability?.score_first_date ? (
                      <div className="text-muted-foreground">
                        Signals collected since {formatDate(availability.score_first_date)}
                      </div>
                    ) : (
                      <div className="text-muted-foreground">Starts on first AI run.</div>
                    )}
                    <div className="mt-1.5">
                      <Progress value={progress} className="h-1.5" data-testid="backtest-score-progress" />
                    </div>
                  </div>
                </div>
                <div className="rounded-md border bg-muted/30 p-3 text-xs leading-relaxed">
                  <strong>Both are needed.</strong> Score history is the limiting factor.
                  {availability?.estimated_ready_date ? (
                    <>
                      {" "}Backtesting will be fully enabled around{" "}
                      <strong>{formatDate(availability.estimated_ready_date)}</strong>.
                    </>
                  ) : null}{" "}
                  You can run partial backtests now, but results are limited to the{" "}
                  {scoreDays} day{scoreDays === 1 ? "" : "s"} of signal data collected so far.
                </div>
              </div>
            );
          })()}
        </CardContent>
      </Card>

      {/* CONFIGURATION */}
      <Card data-testid="backtest-config-card">
        <CardHeader>
          <CardTitle className="text-base">Backtest Configuration</CardTitle>
          <CardDescription className="text-xs">
            "If I had bought every BUY signal stock in this date range and held until the end, what would my return have been?"
          </CardDescription>
        </CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <div>
            <Label className="text-xs">Start date</Label>
            <Input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              data-testid="backtest-start-date"
            />
          </div>
          <div>
            <Label className="text-xs">End date</Label>
            <Input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              data-testid="backtest-end-date"
            />
          </div>
          <div>
            <Label className="text-xs">Investment per signal (LKR)</Label>
            <Input
              type="number"
              value={investment}
              onChange={(e) => setInvestment(e.target.value)}
              data-testid="backtest-investment"
            />
          </div>
          <div>
            <Label className="text-xs">Minimum score</Label>
            <Input
              type="number"
              min={0}
              max={100}
              value={minScore}
              onChange={(e) => setMinScore(e.target.value)}
              data-testid="backtest-min-score"
            />
          </div>
          <div>
            <Label className="text-xs">Signal filter</Label>
            <Select value={signalFilter} onValueChange={setSignalFilter}>
              <SelectTrigger data-testid="backtest-signal-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="BUY">BUY only</SelectItem>
                <SelectItem value="BUY_WATCH">BUY + WATCH</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center gap-2 pt-5">
            <Switch
              checked={includeBrokerage}
              onCheckedChange={setIncludeBrokerage}
              id="brokerage"
              data-testid="backtest-brokerage-toggle"
            />
            <Label htmlFor="brokerage" className="text-xs cursor-pointer">
              Include CSE brokerage (1.21% buy + 1.21% sell)
            </Label>
          </div>
          <div className="md:col-span-2 lg:col-span-3 flex justify-end">
            <Button
              onClick={runBacktest}
              disabled={running}
              data-testid="run-backtest-button"
            >
              {running ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Beaker className="h-4 w-4 mr-2" />}
              {running ? "Running…" : "Run Backtest"}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* RESULTS */}
      {results && <BacktestResults results={results} />}
    </div>
  );
};

const BacktestResults = ({ results }) => {
  const summary = results?.summary || {};
  const rows = results?.rows || [];

  if (rows.length === 0) {
    return (
      <Card data-testid="backtest-empty-results">
        <CardHeader>
          <CardTitle className="text-base">No matching signals</CardTitle>
        </CardHeader>
        <CardContent className="text-sm text-muted-foreground">
          No BUY/WATCH signals were recorded in this date range with score ≥ {results?.config?.min_score}.
          Try widening the date range or lowering the score threshold.
        </CardContent>
      </Card>
    );
  }

  const sorted = [...rows].sort((a, b) => b.return_pct - a.return_pct);

  return (
    <div className="space-y-4" data-testid="backtest-results">
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <Activity className="h-4 w-4 text-primary" />
            Summary — {summary.total_signals} signals
          </CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
          <div>
            <div className="text-xs text-muted-foreground">Win rate</div>
            <div className="text-xl font-semibold tabular-nums">
              {summary.win_rate_pct}%
            </div>
            <div className="text-xs text-muted-foreground">
              {summary.profitable} profitable / {summary.losing} losing
            </div>
          </div>
          <div>
            <div className="text-xs text-muted-foreground">Average return</div>
            <div className={`text-xl font-semibold tabular-nums ${summary.average_return_pct >= 0 ? "text-[hsl(var(--success))]" : "text-[hsl(var(--destructive))]"}`}>
              {formatPct(summary.average_return_pct)}
            </div>
            <div className="text-xs text-muted-foreground">incl. brokerage + dividends</div>
          </div>
          <div>
            <div className="text-xs text-muted-foreground">Best</div>
            <div className="text-xl font-semibold tabular-nums text-[hsl(var(--success))]">
              {summary.best ? `+${summary.best.return_pct}%` : "—"}
            </div>
            <div className="text-xs text-muted-foreground">{summary.best?.cse_symbol}</div>
          </div>
          <div>
            <div className="text-xs text-muted-foreground">Worst</div>
            <div className="text-xl font-semibold tabular-nums text-[hsl(var(--destructive))]">
              {summary.worst ? `${summary.worst.return_pct}%` : "—"}
            </div>
            <div className="text-xs text-muted-foreground">{summary.worst?.cse_symbol}</div>
          </div>
          {summary.aspi_return_pct != null && (
            <div className="md:col-span-4 text-xs text-muted-foreground border-t border-border pt-3">
              ASPI return over the same period: <strong className={summary.aspi_return_pct >= 0 ? "text-[hsl(var(--success))]" : "text-[hsl(var(--destructive))]"}>{formatPct(summary.aspi_return_pct)}</strong>
              {" — "}
              <span>
                Strategy {summary.average_return_pct - summary.aspi_return_pct >= 0 ? "outperformed" : "underperformed"} the market by{" "}
                <strong>{formatPct(summary.average_return_pct - summary.aspi_return_pct)}</strong>
              </span>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm">Per-signal results</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="backtest-results-table">
              <thead className="bg-muted/40 text-xs text-muted-foreground">
                <tr>
                  <th className="px-3 py-2 text-left">Stock</th>
                  <th className="px-3 py-2 text-left">Signal date</th>
                  <th className="px-3 py-2 text-right">Score</th>
                  <th className="px-3 py-2 text-right">Entry</th>
                  <th className="px-3 py-2 text-right">Exit</th>
                  <th className="px-3 py-2 text-right">Net dividend / share</th>
                  <th className="px-3 py-2 text-right">Net P/L</th>
                  <th className="px-3 py-2 text-right">Return %</th>
                </tr>
              </thead>
              <tbody>
                {sorted.map((r) => (
                  <tr key={`${r.cse_symbol}-${r.signal_date}`} className="border-t border-border" data-testid={`backtest-row-${r.company_code}`}>
                    <td className="px-3 py-2">
                      <Link to={`/stocks/${r.company_code}`} className="font-medium hover:underline">
                        {r.cse_symbol}
                      </Link>
                      {r.sector && <div className="text-[11px] text-muted-foreground">{r.sector}</div>}
                    </td>
                    <td className="px-3 py-2">{formatDate(r.signal_date)}</td>
                    <td className="px-3 py-2 text-right tabular-nums">
                      <Badge variant={r.signal === "BUY" ? "default" : "secondary"} className="text-[10px]">
                        {r.signal_score}
                      </Badge>
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums">{r.entry_price}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{r.exit_price}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{r.dividend_per_share_net?.toFixed(2)}</td>
                    <td className={`px-3 py-2 text-right tabular-nums ${r.net_pl_lkr >= 0 ? "text-[hsl(var(--success))]" : "text-[hsl(var(--destructive))]"}`}>
                      {formatLKR(r.net_pl_lkr, { compact: true })}
                    </td>
                    <td className={`px-3 py-2 text-right tabular-nums font-semibold ${r.return_pct >= 0 ? "text-[hsl(var(--success))]" : "text-[hsl(var(--destructive))]"}`}>
                      {r.return_pct >= 0 ? "+" : ""}{r.return_pct}%
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default BacktestPage;
